import React from "react";

import { coalesce } from "@kazoohr/helpers";

import { MultiSelectItem } from "./MultiSelectItem";
import MultiSelectSuggestion from "./MultiSelectSuggestion";

/**
 * Props for the NestedItem component
 */
interface NestedItemProps<T extends string = string> {
  /** The item to display */
  item: MultiSelectItem<T> & {
    _parent?: number;
    _treeLevel?: number;
    _isItemSelectable?: boolean;
    children?: Array<
      MultiSelectItem<T> & {
        _parent?: number;
        _treeLevel?: number;
        children?: unknown[];
      }
    >;
  };
  /** Original array of items */
  originalItems: Array<
    MultiSelectItem<T> & {
      _parent?: number;
      _treeLevel?: number;
      _isItemSelectable?: boolean;
      children?: Array<
        MultiSelectItem<T> & {
          _parent?: number;
          _treeLevel?: number;
          children?: unknown[];
        }
      >;
    }
  >;
  /** Currently selected items */
  selectedItems: MultiSelectItem<T>[];
  /** Function called when selection changes */
  onSelectionChange: (item: MultiSelectItem<T>) => void;
  /** IDs of expanded items */
  expandedItems: T[];
  /** Function called when an item is expanded */
  onItemExpanded: (item: MultiSelectItem<T>) => void;
  /** Whether the parent item is selected */
  isParentSelected?: boolean;
  /** Whether the data is nested */
  nestedData?: boolean;
  /** Whether to behave as a filter */
  behaveAsAFilter?: boolean;
}

// https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
// The following is a call signature since React.forwardRef doesn't provide a way to pass generics from the caller
// to the NestedItemProps.
type WithForwardRefProps<T extends string> = NestedItemProps<T> &
  React.HTMLAttributes<HTMLLIElement>;

interface WithForwardRefType extends React.FC<WithForwardRefProps<string>> {
  <T extends string>(props: WithForwardRefProps<T>): ReturnType<
    React.FC<WithForwardRefProps<T>>
  >;
}

export const NestedItem: WithForwardRefType = React.forwardRef<
  HTMLLIElement,
  NestedItemProps & React.HTMLAttributes<HTMLLIElement>
>(
  // eslint-disable-next-line max-lines-per-function
  (
    {
      item,
      originalItems,
      selectedItems,
      onSelectionChange,
      expandedItems,
      onItemExpanded,
      isParentSelected,
      nestedData,
      behaveAsAFilter,
      ...props
    },
    ref
  ) => {
    const getParent = (item: NestedItemProps["item"]) => {
      return item._parent === undefined
        ? undefined
        : originalItems[item._parent];
    };

    const isOneOfMyGrandChildrenSelected = (item: NestedItemProps["item"]) => {
      const descendants = getArrayOfDescendantItems(item);
      return descendants.some((descendant) =>
        selectedItems.some((selectedItem) => selectedItem.id === descendant.id)
      );
    };

    const getNearestSelectedAncestor = (item: NestedItemProps["item"]) => {
      let cursor = getParent(item);
      while (cursor !== undefined) {
        if (
          selectedItems.some((selectedItem) => selectedItem.id === cursor?.id)
        ) {
          break;
        }
        cursor = getParent(cursor);
      }
      return cursor;
    };

    const getArrayOfAncestorIndexes = (item: NestedItemProps["item"]) => {
      const array: number[] = [];
      let cursor = item;
      while (cursor._parent !== undefined) {
        array.push(cursor._parent);
        cursor = coalesce(getParent(cursor), cursor);
      }
      return array;
    };

    const getArrayOfDescendantItems = (item: NestedItemProps["item"]) => {
      let descendants: NestedItemProps["item"][] = [];
      let toTraverse: NestedItemProps["item"][] = [];
      toTraverse.push(item);

      // Traverse all children
      while (toTraverse.length > 0) {
        const currentItem = toTraverse.pop();

        if (currentItem?.children) {
          toTraverse = [...toTraverse, ...currentItem.children];
          descendants = [...descendants, ...currentItem.children];
        }
      }
      return descendants;
    };

    const onChange = () => {
      // istanbul ignore else - MultiSelectSuggestion disables items that are not selectable
      if (isItemSelectable) {
        // If the item is indeterminate, select all children and remove
        // descendants from the input list
        if (isIndeterminate && behaveAsAFilter) {
          const selectedChildren = getArrayOfDescendantItems(
            originalItem
          ).filter((i) => selectedItems.map((s) => s.id).includes(i.id));

          selectedChildren.forEach((i) => {
            onSelectionChange(i);
          });
          onSelectionChange(originalItem);
          return;
        }

        // If the clicked element has a parent
        if (directParent && behaveAsAFilter) {
          const nearestSelectedAncestor =
            getNearestSelectedAncestor(originalItem);
          const ancestorsIndexes = [...getArrayOfAncestorIndexes(originalItem)];
          // If an ancestor is selected, it means that the clicked element must be unselected
          if (nearestSelectedAncestor) {
            let shouldCompute = false;

            // Get rid of the parent
            onSelectionChange(nearestSelectedAncestor);

            // Loop from the first level of the tree to the level of the clicked element
            for (let i = ancestorsIndexes.length - 1; i >= 0; i--) {
              const ancestorIdx = ancestorsIndexes[i];
              if (
                nearestSelectedAncestor.id === originalItems[ancestorIdx].id ||
                shouldCompute
              ) {
                shouldCompute = true;
                const dontSelect =
                  i === 0
                    ? originalItem.id
                    : originalItems[ancestorsIndexes[i - 1]].id;
                originalItems[ancestorIdx].children
                  ?.filter((children) => children.id !== dontSelect)
                  .forEach((children) => onSelectionChange(children));
              }
            }
            return;
          }

          const toDelete: NestedItemProps["item"][] = [];
          let toPush: NestedItemProps["item"][] = [];
          // Check from the level of the clicked element to the first level of the tree
          for (let i = 0; i < ancestorsIndexes.length; i++) {
            const ancestorIdx = ancestorsIndexes[i];
            const ancestor = originalItems[ancestorIdx];
            const dontSelect =
              i === 0
                ? originalItem.id
                : originalItems[ancestorsIndexes[i - 1]].id;

            const ancestorChildren = coalesce(ancestor.children, []);
            const selectedSiblings = ancestorChildren.filter((children) =>
              selectedItems.some((sItem) => sItem.id === children.id)
            );
            if (
              selectedSiblings.length + 1 === ancestorChildren.length &&
              !selectedItems.some((i) => i.id === originalItem.id)
            ) {
              toDelete.push(
                ...ancestorChildren.filter(
                  (children) => children.id !== dontSelect
                )
              );
              toPush = [ancestor];
            } else {
              break;
            }
          }
          if (toDelete.length > 0 || toPush.length > 0) {
            toDelete.forEach((item) => onSelectionChange(item));
            toPush.forEach((item) => onSelectionChange(item));
            return;
          }
        }
        onSelectionChange(originalItem);
      }
    };

    /** ************ RENDER LOGIC **************/
    const originalItem = !nestedData
      ? item
      : originalItems.filter((originalItem) => originalItem.id === item.id)[0];
    const isItemSelectable =
      originalItem._isItemSelectable !== undefined
        ? originalItem._isItemSelectable
        : true;

    // If element does not include nested results, render a simple row and avoid
    // further computing
    if (
      !nestedData ||
      (originalItem._parent === undefined &&
        originalItem.children === undefined)
    ) {
      const isSelected = selectedItems.some(
        (item) => item.id === originalItem.id
      );
      return (
        <MultiSelectSuggestion
          {...props}
          onChange={() => onSelectionChange(originalItem)}
          key={originalItem.id}
          item={originalItem}
          isSelected={isSelected}
          isItemSelectable={isItemSelectable}
          hasChildren={false}
          ref={ref}
        />
      );
    }

    const isIndeterminateFn = () => {
      if (!hasChildren || (isSelected && !behaveAsAFilter)) {
        return false;
      }
      if (
        howManyChildrenSelected < originalChildren.length &&
        howManyChildrenSelected > 0
      ) {
        return true;
      }
      return isOneOfMyGrandChildrenSelected(originalItem);
    };

    const isSelectedFn = () => {
      if (selectedItems.some((item) => item.id === originalItem.id)) {
        return true;
      }
      if (!behaveAsAFilter) {
        return false;
      }
      if (
        hasChildren &&
        originalItem.children?.length === howManyChildrenSelected
      ) {
        return true;
      }
      return Boolean(isParentSelected);
    };

    const directParent = getParent(originalItem);
    const hasChildren =
      originalItem.children && originalItem.children.length > 0;
    const displayChildren = hasChildren
      ? expandedItems.some((id) => id === item.id)
      : false;
    const originalChildren = coalesce(originalItem.children, []);
    const howManyChildrenSelected = originalChildren.filter((children) =>
      selectedItems.some((sItem) => sItem.id === children.id)
    ).length;
    const isSelected = isSelectedFn();
    const isIndeterminate = isIndeterminateFn();

    let childrenRender;
    if (displayChildren && item.children) {
      childrenRender = item.children.map((children) => (
        <NestedItem
          {...props}
          key={children.id}
          item={children}
          originalItems={originalItems}
          selectedItems={selectedItems}
          onSelectionChange={onSelectionChange}
          expandedItems={expandedItems}
          onItemExpanded={onItemExpanded}
          isParentSelected={isSelected}
          nestedData={nestedData}
          behaveAsAFilter={behaveAsAFilter}
        />
      ));
    }

    return (
      <div>
        <MultiSelectSuggestion
          {...props}
          item={originalItem}
          isSelected={isSelected}
          key={item.id}
          isIndeterminate={isIndeterminate}
          isItemSelectable={isItemSelectable}
          displayChildren={displayChildren}
          hasChildren={hasChildren}
          onDisplayChildrenChange={onItemExpanded}
          onChange={onChange}
          nestedData={nestedData}
        />
        {childrenRender}
      </div>
    );
  }
);

export default NestedItem;
